import { Capacitor } from '@capacitor/core';
import { BottomNavigation, Box, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PortalTheme } from '@frontend-libs/portal';

import { siteUnitClient } from '@portal-clients/site-unit';

import { useCountNewsQuery, useCountVoucherQuery } from '@frontend/data';
import { useClientQuery } from '@frontend/hooks';
import { useLicenses } from '@frontend/licenses';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';
import {
  routes,
  useOptionalPortalConfiguration,
  useOptionalUser,
} from '@frontend/utils';

import { AppBottomNavbarAction } from './app-bottom-navbar-action.component';
import { MenuActions } from './menu-actions';
import { shouldSkipNavbarNavigation } from './should-skip-navbar-navigation';

export interface AppBottomNavbarProps {
  extraContent?: React.ReactNode;
}

export const AppBottomNavbar = (props: AppBottomNavbarProps) => {
  const locationPath = useLocation().pathname;
  const theme = useTheme<PortalTheme>();
  const navigate = useNavigate();
  const { user } = useOptionalUser();
  const config = useOptionalPortalConfiguration();
  const { content: publicContent, features: publicFeatures } =
    usePortalPublicConfiguration();

  const activeRoute = () => {
    if (locationPath.startsWith(routes.siteUnits.sites)) {
      return routes.siteUnits.sites;
    }

    if (locationPath.startsWith(routes.news())) {
      return routes.news();
    }

    if (locationPath.startsWith(routes.benefits)) {
      return routes.benefits;
    }

    if (locationPath.startsWith(routes.wallet.list())) {
      return routes.wallet.list();
    }

    if (locationPath.startsWith(routes.home)) {
      return routes.home;
    }

    return routes.wallet.list();
  };

  const { data: siteUnits } = useClientQuery({
    queryKey: ['site-units'],
    onFetch: async () => {
      return await siteUnitClient.search();
    },
  });

  const {
    news: newsLicense,
    vouchers: vouchersLicense,
    stampCards: stampCardsLicense,
  } = useLicenses();

  const isNewsEnabled = useMemo(() => {
    return newsLicense && user
      ? (config?.features?.news?.enabled ?? false)
      : (publicFeatures?.news?.enabled ?? false);
  }, [user, publicFeatures, config, newsLicense]);

  const isHomeEnabled = useMemo(() => {
    return user
      ? (config?.features?.home?.enabled ?? false)
      : (publicFeatures?.home?.enabled ?? false);
  }, [user, publicFeatures, config]);

  const { data: newsCount } = useCountNewsQuery({
    onError() {
      captureException('Failed to fetch news count', { level: 'warning' });
    },
  });

  const { data: voucherCount } = useCountVoucherQuery({
    onError() {
      captureException('Failed to fetch voucher count', { level: 'warning' });
    },
  });

  const actions = useMemo(() => {
    return MenuActions({
      siteUnits,
      locationPath,
      theme,
      user,
      config,
      content: config?.content ?? publicContent,
      newsLicense,
      vouchersLicense,
      stampCardsLicense,
      isNewsEnabled,
      newsCount: newsCount?.count ?? 0,
      voucherCount: voucherCount?.count ?? 0,
      isHomeEnabled,
    });
  }, [
    siteUnits,
    locationPath,
    theme,
    user,
    config,
    publicContent,
    newsLicense,
    vouchersLicense,
    stampCardsLicense,
    isNewsEnabled,
    newsCount,
    voucherCount,
    isHomeEnabled,
  ]);

  return (
    <Box
      sx={{
        width: '100%',
        bottom: 0,
        left: 0,
        right: 0,
        position: 'fixed',
        backgroundColor: theme.palette.background.default,
        pb: Capacitor.isNativePlatform()
          ? 'var(--safe-area-inset-bottom)'
          : undefined,
      }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      {props.extraContent && (
        <Box
          sx={{
            width: '100%',
            maxWidth: theme.contentMaxWidth,
            borderTop: `1px solid ${theme.palette.divider}`,
            p: 2,
          }}
        >
          {props.extraContent}
        </Box>
      )}

      <BottomNavigation
        component="nav"
        showLabels
        value={activeRoute()}
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: theme.bottomNavbarHeight,
          width: '100%',
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {actions.map((action, index) => (
          <AppBottomNavbarAction
            key={index}
            value={action.route}
            label={action.title}
            count={action.count}
            disabled={action.disabled}
            onClick={() => {
              if (
                shouldSkipNavbarNavigation(
                  action.route,
                  routes.wallet.list(),
                  locationPath,
                )
              ) {
                return;
              }

              navigate(action.navigateTo);
            }}
            icon={action.getIcon()}
          />
        ))}
      </BottomNavigation>
    </Box>
  );
};
